// pages/TaskListView.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  ArrowUpDown,
  Grid3x3,
  Plus,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  FileIcon,
  Send,
  CheckCircle,
  Eye,
  X,
  Calendar,
  User,
  Flag,
  BarChart3,
  TrendingUp,
  Users as UsersIcon,
  FileText,
  Clock,
  CheckCircle2,
  Vote as VoteIcon,
  Play
} from 'lucide-react';
import { API_BASE_URL } from '../config/api';

const API_BASE = API_BASE_URL;

interface ProjectData {
  _id: string;
  title: string;
  creator: { _id: string; name: string; email: string };
  teams?: TeamData[];
}

interface TeamData {
  _id: string;
  name: string;
  members: { _id: string; name: string; email: string }[];
}

interface TaskListViewProps {
  projectId: string;
  project: ProjectData | null;
  user: any;
  isCreator: boolean;
  onTaskClick?: (task: any) => void;
  onSubmitTask?: (task: any) => void;
}

const TaskListView: React.FC<TaskListViewProps> = ({ projectId, project, user, isCreator, onTaskClick, onSubmitTask }) => {
  // States
  const [tasks, setTasks] = useState<any[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [sections, setSections] = useState<{ id: string; name: string; status?: string; collapsed: boolean }[]>([
    { id: 'todo', name: 'To do', status: 'todo', collapsed: false },
    { id: 'doing', name: 'Doing', status: 'in-progress', collapsed: false },
    { id: 'review', name: 'Review', status: 'review', collapsed: false },
    { id: 'done', name: 'Done', status: 'done', collapsed: false }
  ]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showStats, setShowStats] = useState(true);
  
  // Task files, submissions, validations
  const [taskFiles, setTaskFiles] = useState<Record<string, any[]>>({});
  const [taskSubmissions, setTaskSubmissions] = useState<Record<string, any>>({});
  const [taskValidations, setTaskValidations] = useState<Record<string, any[]>>({});
  const [taskValidationVotes, setTaskValidationVotes] = useState<Record<string, any>>({});
  const [userValidationVotes, setUserValidationVotes] = useState<Record<string, any>>({});

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    byStatus: {} as Record<string, number>,
    byAssignee: {} as Record<string, { name: string; count: number; completed: number; inProgress: number; submitted: number }>,
    withFiles: 0,
    submitted: 0,
    validated: 0,
    pendingValidation: 0
  });

  // Fetch tasks
  const fetchTasks = async () => {
    if (!projectId || !user?._id) return;
    setTasksLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/tasks/${projectId}?userId=${user._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const responseData = res.data?.data;
      let fetchedTasks: any[] = [];
      if (responseData && Array.isArray(responseData.tasks)) {
        fetchedTasks = responseData.tasks;
        setTasks(fetchedTasks);
      } else if (Array.isArray(responseData)) {
        fetchedTasks = responseData;
        setTasks(fetchedTasks);
      } else {
        setTasks([]);
      }
      
      // Fetch files, submissions and validations for all tasks
      if (fetchedTasks.length > 0) {
        fetchedTasks.forEach(task => {
          fetchTaskFiles(task._id);
          fetchTaskSubmission(task._id); // Cette fonction récupère maintenant aussi les votes si soumis
          fetchTaskValidations(task._id);
        });
      }

      // Calculate stats
      calculateStats(fetchedTasks);
    } catch (err: any) {
      console.error('Erreur chargement tâches:', err);
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  // Calculate statistics
  const calculateStats = (tasksList: any[]) => {
    const newStats = {
      total: tasksList.length,
      byStatus: {} as Record<string, number>,
      byAssignee: {} as Record<string, { name: string; count: number; completed: number; inProgress: number; submitted: number }>,
      withFiles: 0,
      submitted: 0,
      validated: 0,
      pendingValidation: 0
    };

    tasksList.forEach(task => {
      // By status
      const status = task.status || 'todo';
      newStats.byStatus[status] = (newStats.byStatus[status] || 0) + 1;

      // By assignee
      const assigneeId = task.assignee?._id || task.assignee || 'unassigned';
      const assigneeName = task.assignee?.name || 'Unassigned';
      
      if (!newStats.byAssignee[assigneeId]) {
        newStats.byAssignee[assigneeId] = {
          name: assigneeName,
          count: 0,
          completed: 0,
          inProgress: 0,
          submitted: 0
        };
      }
      newStats.byAssignee[assigneeId].count++;
      if (status === 'done') newStats.byAssignee[assigneeId].completed++;
      if (status === 'in-progress') newStats.byAssignee[assigneeId].inProgress++;
      if (taskSubmissions[task._id]) newStats.byAssignee[assigneeId].submitted++;
    });

    // Files, submissions, validations
    tasksList.forEach(task => {
      if (taskFiles[task._id]?.length > 0) newStats.withFiles++;
      if (taskSubmissions[task._id]) {
        newStats.submitted++;
        const validations = taskValidations[task._id] || [];
        if (validations.length > 0) {
          newStats.validated++;
        } else {
          newStats.pendingValidation++;
        }
      }
    });

    setStats(newStats);
  };

  // Fetch task files
  const fetchTaskFiles = async (taskId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/tasks/${taskId}/files`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setTaskFiles(prev => ({ ...prev, [taskId]: res.data.data || [] }));
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Erreur récupération fichiers:', err);
      }
      setTaskFiles(prev => ({ ...prev, [taskId]: [] }));
    }
  };

  // Fetch task submission
  const fetchTaskSubmission = async (taskId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/tasks/${taskId}/submission`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setTaskSubmissions(prev => ({ ...prev, [taskId]: res.data.data }));
        // Si la soumission est en statut 'submitted', récupérer les votes automatiquement
        if (res.data.data && (res.data.data.status === 'submitted' || res.data.data.status === 'under-review')) {
          await fetchTaskValidationVotes(taskId);
        }
        return res.data.data;
      }
      return null;
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Erreur récupération soumission:', err);
      }
      return null;
    }
  };

  // Fetch task validations
  const fetchTaskValidations = async (taskId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/tasks/${taskId}/validations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setTaskValidations(prev => ({ ...prev, [taskId]: res.data.data || [] }));
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Erreur récupération validations:', err);
      }
      setTaskValidations(prev => ({ ...prev, [taskId]: [] }));
    }
  };

  // Fetch task validation votes
  const fetchTaskValidationVotes = async (taskId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/tasks/${taskId}/validation-votes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setTaskValidationVotes(prev => ({ ...prev, [taskId]: res.data.voteResults || {} }));
        
        // Trouver le vote de l'utilisateur actuel
        const userVote = res.data.data.find((v: any) => 
          v.voter && (v.voter._id === user?._id || v.voter === user?._id)
        );
        if (userVote) {
          setUserValidationVotes(prev => ({ ...prev, [taskId]: userVote }));
        } else {
          setUserValidationVotes(prev => ({ ...prev, [taskId]: null }));
        }
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Erreur récupération votes de validation:', err);
      }
      setTaskValidationVotes(prev => ({ ...prev, [taskId]: {} }));
    }
  };

  // Open task detail modal
  const openTaskDetailModal = async (task: any) => {
    if (onTaskClick) {
      onTaskClick(task);
    }
  };

  useEffect(() => {
    if (projectId && user?._id) {
      fetchTasks();
    }
  }, [projectId, user?._id]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      fetchTasks();
    };
    window.addEventListener('refreshTaskList', handleRefresh);
    return () => {
      window.removeEventListener('refreshTaskList', handleRefresh);
    };
  }, []);

  // Recalculate stats when data changes
  useEffect(() => {
    if (tasks.length > 0) {
      calculateStats(tasks);
    }
  }, [tasks, taskFiles, taskSubmissions, taskValidations]);

  // Get all team members
  const getAllTeamMembers = () => {
    if (!project?.teams) return [];
    const allMembers: any[] = [];
    const memberMap = new Map();
    
    project.teams.forEach(team => {
      if (team.members && Array.isArray(team.members)) {
        team.members.forEach((member: any) => {
          if (!memberMap.has(member._id)) {
            memberMap.set(member._id, member);
            allMembers.push(member);
          }
        });
      }
    });
    
    return allMembers;
  };

  // Filter tasks
  const getFilteredTasks = () => {
    let filtered = [...tasks];

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(task =>
        task.title?.toLowerCase().includes(query) ||
        task.description?.toLowerCase().includes(query) ||
        task.assignee?.name?.toLowerCase().includes(query) ||
        task.team?.name?.toLowerCase().includes(query)
      );
    }

    // Filter by assignee
    if (selectedAssignee !== 'all') {
      filtered = filtered.filter(task => {
        const assigneeId = task.assignee?._id || task.assignee || 'unassigned';
        return assigneeId === selectedAssignee;
      });
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(task => task.status === selectedStatus);
    }

    return filtered;
  };

  const filteredTasks = getFilteredTasks();

  return (
    <div className="space-y-4">
      {/* Statistics Dashboard */}
      {showStats && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Avancement du travail
            </h3>
            <button
              onClick={() => setShowStats(!showStats)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {showStats ? 'Masquer' : 'Afficher'}
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="text-xs text-blue-600 font-medium mb-1">Total</div>
              <div className="text-2xl font-bold text-blue-900">{stats.total}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <div className="text-xs text-green-600 font-medium mb-1">Completed</div>
              <div className="text-2xl font-bold text-green-900">{stats.byStatus.done || 0}</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
              <div className="text-xs text-amber-600 font-medium mb-1">En cours</div>
              <div className="text-2xl font-bold text-amber-900">{stats.byStatus['in-progress'] || 0}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <div className="text-xs text-purple-600 font-medium mb-1">Avec fichiers</div>
              <div className="text-2xl font-bold text-purple-900">{stats.withFiles}</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
              <div className="text-xs text-yellow-600 font-medium mb-1">Soumises</div>
              <div className="text-2xl font-bold text-yellow-900">{stats.submitted}</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
              <div className="text-xs text-orange-600 font-medium mb-1">En attente</div>
              <div className="text-2xl font-bold text-orange-900">{stats.pendingValidation}</div>
            </div>
            <div className="bg-teal-50 rounded-lg p-3 border border-teal-200">
              <div className="text-xs text-teal-600 font-medium mb-1">Validated</div>
              <div className="text-2xl font-bold text-teal-900">{stats.validated}</div>
            </div>
          </div>

          {/* Stats by assignee */}
          {Object.keys(stats.byAssignee).length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <UsersIcon className="h-4 w-4" />
                Par membre
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(stats.byAssignee).map(([assigneeId, data]) => (
                  <div key={assigneeId} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-900">{data.name}</span>
                      </div>
                      <span className="text-xs text-gray-500">{data.count} tasks</span>
                    </div>
                    <div className="flex gap-2 text-xs">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                        ✓ {data.completed}
                      </span>
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded">
                        ⏳ {data.inProgress}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        📤 {data.submitted}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-[300px]">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Filter by assignee */}
            <select
              value={selectedAssignee}
              onChange={(e) => setSelectedAssignee(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les membres</option>
              <option value="unassigned">Unassigned</option>
              {getAllTeamMembers().map((member: any) => (
                <option key={member._id} value={member._id}>
                  {member.name || member.email}
                </option>
              ))}
            </select>

            {/* Filter by status */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="todo">To do</option>
              <option value="in-progress">En cours</option>
              <option value="review">Review</option>
              <option value="done">Completed</option>
              <option value="submitted">Soumis</option>
              <option value="under-review">Under review</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tasks List */}
      {tasksLoading ? (
        <div className="flex items-center justify-center py-12 bg-white rounded-lg border border-gray-200">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Column Header */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-600">
              <div className="col-span-4">Nom</div>
              <div className="col-span-2">Assigned to</div>
              <div className="col-span-2">Deadline</div>
              <div className="col-span-2">Priority</div>
              <div className="col-span-2">Statut</div>
            </div>
          </div>

          {/* Section "En attente de validation" pour le créateur */}
          {isCreator && (() => {
            const pendingValidationTasks = filteredTasks.filter(task => {
              const submission = taskSubmissions[task._id];
              if (!submission) return false;
              const validations = taskValidations[task._id] || [];
              // Exclure les tâches déjà validées (approved)
              if (validations.length > 0 && validations[0].status === 'approved') return false;
              // Exclure les tâches dont la soumission est déjà approuvée
              if (submission.status === 'approved') return false;
              return submission.status === 'submitted' || 
                     (validations.length > 0 && validations[0].status === 'revision-requested');
            });

            if (pendingValidationTasks.length > 0) {
              return (
                <div key="pending-validation" className="bg-yellow-50 border-2 border-yellow-300 rounded-lg mb-4">
                  <div className="p-4 border-b border-yellow-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-yellow-600" />
                        <h3 className="text-sm font-bold text-yellow-900">
                          ⚠️ Pending validation ({pendingValidationTasks.length})
                        </h3>
                      </div>
                      <span className="text-xs font-semibold text-yellow-700 bg-yellow-200 px-3 py-1 rounded-full">
                        Action requise
                      </span>
                    </div>
                    <p className="text-xs text-yellow-800 mt-2">
                      Click a task to validate it with feedback
                    </p>
                  </div>
                  <div className="divide-y divide-yellow-100">
                    {pendingValidationTasks.map((task) => (
                      <div key={task._id} className="bg-white">
                        <TaskRow
                          task={task}
                          onUpdate={fetchTasks}
                          project={project}
                          user={user}
                          onTaskClick={openTaskDetailModal}
                          onSubmitTask={onSubmitTask}
                          projectId={projectId}
                          taskFiles={taskFiles}
                          taskSubmissions={taskSubmissions}
                          taskValidations={taskValidations}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* Section "En attente de vote" pour les membres simples (non créateurs) */}
          {!isCreator && (() => {
            const pendingVoteTasks = filteredTasks.filter(task => {
              const submission = taskSubmissions[task._id];
              if (!submission || submission.status !== 'submitted') return false;
              
              // Exclure les tâches déjà validées
              const validations = taskValidations[task._id] || [];
              if (validations.length > 0 && validations[0].status === 'approved') return false;
              if (submission.status === 'approved') return false;
              
              // Vérifier que l'utilisateur n'est pas celui qui a soumis la tâche
              const submittedBy = submission.submittedBy 
                ? (typeof submission.submittedBy === 'object' ? submission.submittedBy._id : submission.submittedBy)
                : null;
              if (submittedBy === user?._id) return false;
              
              // Vérifier que l'utilisateur n'a pas encore voté (ou peut modifier son vote)
              // On affiche la tâche même si l'utilisateur a déjà voté, pour qu'il puisse voir/modifier son vote
              return true;
            });

            if (pendingVoteTasks.length > 0) {
              return (
                <div key="pending-vote" className="bg-purple-50 border-2 border-purple-300 rounded-lg mb-4">
                  <div className="p-4 border-b border-purple-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <VoteIcon className="h-5 w-5 text-purple-600" />
                        <h3 className="text-sm font-bold text-purple-900">
                          📊 Pending vote ({pendingVoteTasks.length})
                        </h3>
                      </div>
                      <span className="text-xs font-semibold text-purple-700 bg-purple-200 px-3 py-1 rounded-full">
                        Action requise
                      </span>
                    </div>
                    <p className="text-xs text-purple-800 mt-2">
                      Click a task to vote on its validation
                    </p>
                  </div>
                  <div className="divide-y divide-purple-100">
                    {pendingVoteTasks.map((task) => {
                      const submission = taskSubmissions[task._id];
                      const userVote = userValidationVotes[task._id];
                      const hasVoted = !!userVote;
                      
                      return (
                        <div key={task._id} className="bg-white">
                          <div className="flex items-center justify-between p-3">
                            <div className="flex-1">
                              <TaskRow
                                task={task}
                                onUpdate={fetchTasks}
                                project={project}
                                user={user}
                                onTaskClick={onTaskClick}
                                onSubmitTask={onSubmitTask}
                                projectId={projectId}
                                taskFiles={taskFiles}
                                taskSubmissions={taskSubmissions}
                                taskValidations={taskValidations}
                              />
                            </div>
                            {hasVoted && (
                              <div className="ml-4 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                ✓ Voted: {userVote.vote === 'valid' ? 'Valid' : 'Invalid'}
                              </div>
                            )}
                            {!hasVoted && (
                              <div className="ml-4 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                                ⚠️ Vote required
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* Sections */}
          {sections.map((section) => {
            const sectionTasks = filteredTasks.filter(task => {
              if (section.status) {
                return task.status === section.status;
              }
              return false;
            }).filter(task => {
              // Exclure les tâches déjà affichées dans "En attente de validation" (créateur)
              if (isCreator) {
                const submission = taskSubmissions[task._id];
                if (submission) {
                  const validations = taskValidations[task._id] || [];
                  const isPending = submission.status === 'submitted' || 
                                   (validations.length > 0 && validations[0].status === 'revision-requested');
                  if (isPending) return false;
                }
              }
              
              // Exclure les tâches déjà affichées dans "En attente de vote" (membres simples)
              if (!isCreator) {
                const submission = taskSubmissions[task._id];
                if (submission && submission.status === 'submitted') {
                  const submittedBy = submission.submittedBy 
                    ? (typeof submission.submittedBy === 'object' ? submission.submittedBy._id : submission.submittedBy)
                    : null;
                  // Exclure si ce n'est pas l'utilisateur qui a soumis (donc c'est dans "En attente de vote")
                  if (submittedBy !== user?._id) return false;
                }
              }
              
              return true;
            });

            return (
              <div key={section.id} className="bg-white rounded-lg border border-gray-200">
                <button
                  onClick={() => {
                    setSections(prev => prev.map(s =>
                      s.id === section.id ? { ...s, collapsed: !s.collapsed } : s
                    ));
                  }}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {section.collapsed ? (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                    <h3 className="text-sm font-semibold text-gray-900">{section.name}</h3>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {sectionTasks.length}
                    </span>
                  </div>
                </button>

                {!section.collapsed && (
                  <div className="border-t border-gray-200">
                    {sectionTasks.length === 0 ? (
                      <div className="p-8 text-center text-gray-400">
                        No tasks
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {sectionTasks.map((task) => (
                          <TaskRow
                            key={task._id}
                            task={task}
                            onUpdate={fetchTasks}
                            project={project}
                            user={user}
                            onTaskClick={openTaskDetailModal}
                            onSubmitTask={onSubmitTask}
                            projectId={projectId}
                            taskFiles={taskFiles}
                            taskSubmissions={taskSubmissions}
                            taskValidations={taskValidations}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// TaskRow Component
const TaskRow: React.FC<{
  task: any;
  onUpdate: () => void;
  project: ProjectData | null;
  user: any;
  onTaskClick?: (task: any) => void;
  onSubmitTask?: (task: any) => void;
  projectId?: string;
  taskFiles?: Record<string, any[]>;
  taskSubmissions?: Record<string, any>;
  taskValidations?: Record<string, any[]>;
}> = ({ task, onUpdate, project, user, onTaskClick, onSubmitTask, projectId, taskFiles = {}, taskSubmissions = {}, taskValidations = {} }) => {
  const navigate = useNavigate();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({
    title: task.title,
    status: task.status,
    priority: task.priority,
    deadline: task.deadline ? new Date(task.deadline).toISOString().split('T')[0] : '',
    assignee: task.assignee?._id || ''
  });

  const getAllTeamMembers = () => {
    if (!project?.teams) return [];
    const allMembers: any[] = [];
    const memberMap = new Map();
    
    project.teams.forEach(team => {
      if (team.members && Array.isArray(team.members)) {
        team.members.forEach((member: any) => {
          if (!memberMap.has(member._id)) {
            memberMap.set(member._id, member);
            allMembers.push(member);
          }
        });
      }
    });
    
    return allMembers;
  };

  const handleFieldSave = async (field: string, value: any) => {
    try {
      const updateData: any = {};
      updateData[field] = value;
      
      await axios.put(`${API_BASE}/tasks/${task._id}`, updateData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setEditingField(null);
      onUpdate();
    } catch (err: any) {
      console.error('Erreur mise à jour tâche:', err);
      alert(err.response?.data?.error || 'Error updating');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-purple-100 text-purple-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'bg-teal-100 text-teal-700';
      case 'in-progress': return 'bg-amber-100 text-amber-700';
      case 'blocked': return 'bg-red-100 text-red-700';
      case 'review': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (date: string | Date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="grid grid-cols-12 gap-4 p-4 hover:bg-gray-50 transition-colors items-center">
      <div 
        className="col-span-4 cursor-pointer"
        onClick={(e) => {
          if (onTaskClick) {
            e.stopPropagation();
            onTaskClick(task);
          }
        }}
      >
        <div className="flex items-center gap-2 flex-1">
          <span className="text-sm font-medium text-gray-900 hover:text-blue-600 cursor-pointer flex-1">
            {task.title}
          </span>
          {taskFiles[task._id]?.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
              <FileIcon className="h-3 w-3" />
              {taskFiles[task._id].length}
            </span>
          )}
          {taskSubmissions[task._id] && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs">
              <Send className="h-3 w-3" />
              Soumis
            </span>
          )}
          {taskValidations[task._id]?.length > 0 && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
              taskValidations[task._id][0]?.status === 'approved' ? 'bg-green-100 text-green-700' :
              taskValidations[task._id][0]?.status === 'revision-requested' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              <CheckCircle className="h-3 w-3" />
              {taskValidations[task._id][0]?.status === 'approved' ? 'Validated' :
               taskValidations[task._id][0]?.status === 'revision-requested' ? 'Revision' :
               'Rejected'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-1.5" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTaskClick?.(task);
            }}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors font-medium"
            title="View details, files and validation"
          >
            <Eye className="h-3 w-3" />
            Details
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const teamId = typeof task.team === 'object' ? task.team?._id : task.team;
              const pid = project?._id || projectId;
              if (teamId && pid) {
                navigate(`/projects/${pid}/team/${teamId}/planning-poker`);
              } else if (pid) {
                navigate(`/projects/${pid}?tab=planning-poker`);
              }
            }}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100 transition-colors font-medium"
            title="Estimate this task in Planning Poker"
          >
            <Play className="h-3 w-3" />
            Poker
          </button>
          {taskSubmissions[task._id] ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTaskClick?.(task);
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-50 text-amber-700 rounded hover:bg-amber-100 transition-colors font-medium"
              title="View the submitted work"
            >
              <Send className="h-3 w-3" />
              Submission
            </button>
          ) : task.assignee && (task.assignee._id === user?._id || task.assignee === user?._id) &&
            ['todo', 'in-progress', 'revision-requested'].includes(task.status) ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSubmitTask?.(task);
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 transition-colors font-medium"
              title="Submit this task for review"
            >
              <Send className="h-3 w-3" />
              Submit
            </button>
          ) : null}
        </div>
      </div>

      <div className="col-span-2">
        {task.assignee ? (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center font-medium">
              {task.assignee.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <span className="text-sm text-gray-700 truncate">{task.assignee.name || 'Unassigned'}</span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">Unassigned</span>
        )}
      </div>

      <div className="col-span-2">
        <span className="text-sm text-gray-700">{formatDate(task.deadline)}</span>
      </div>

      <div className="col-span-2">
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority || 'medium')}`}>
          {task.priority || 'medium'}
        </span>
      </div>

      <div className="col-span-2">
        {/* Vérifier si l'utilisateur est assigné à la tâche et peut changer le statut */}
        {task.assignee && (task.assignee._id === user?._id || task.assignee === user?._id) && 
         (task.status === 'todo' || task.status === 'in-progress') ? (
          <select
            value={task.status || 'todo'}
            onChange={(e) => {
              e.stopPropagation();
              handleFieldSave('status', e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 ${getStatusColor(task.status || 'todo')}`}
          >
            <option value="todo">To do</option>
            <option value="in-progress">En cours</option>
          </select>
        ) : (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status || 'todo')}`}>
            {task.status === 'in-progress' ? 'En cours' : 
             task.status === 'todo' ? 'To do' : 
             task.status === 'review' ? 'Review' :
             task.status || 'todo'}
          </span>
        )}
      </div>
    </div>
  );
};

export default TaskListView;

